import { Component, createRef, forwardRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import UUID from '../utils/UUID';
import { getOrigin, getIFrameSrc } from '../utils/IFrameSrc';
import { createAppData, createEmbeddedPropsChangeMessage, createHostContext, createSyncMessage, isReadyMessage, isToIFrameHost, validMessage } from 'ui-addon-iframeable/messaging/IFrameControlMessage';
import { logHostError, logHostMessageReceived, logHostMessageSent } from 'ui-addon-iframeable/utils/Logger';
import { isMessageToMe } from 'ui-addon-iframeable/messaging/IFrameMessage';
import { ERROR_EMBED_NOT_READY, ERROR_INVALID_CALLBACK_PROVIDED, ERROR_UNABLE_TO_SYNC } from 'ui-addon-iframeable/messaging/Errors';
import { isNotNull } from '../utils';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledIFrame = styled.iframe.withConfig({
  displayName: "IFrame__StyledIFrame"
})(["display:flex;flex-grow:1;"]);
export class IFrame extends Component {
  constructor(props) {
    super(props);
    this.callbacks = new Map();
    this.syncRetryInterval = 100;
    this.numSyncRetries = 0;
    this.handleReadyTimer = null;
    this.retrySyncTimer = null;
    this.hasCallback = message => {
      return this.callbacks.has(message.id);
    };
    this.validCallback = callback => {
      if (typeof callback === 'function') {
        return true;
      }
      logHostError(this.props.name, ERROR_INVALID_CALLBACK_PROVIDED, JSON.stringify(callback));
      return false;
    };
    this.isFromEmbed = message => {
      return message.from === this.iFrame.uuid;
    };
    this.isToEmbed = message => {
      return isMessageToMe(message, this.iFrame.uuid, this.props.group);
    };
    this.dispatchCallback = message => {
      const callback = this.callbacks.get(message.id);
      if (typeof callback === 'function') {
        callback(message.payload);
      }
      this.callbacks.delete(message.id);
    };
    this._simulatePostMessage = event => {
      this.handleReceiveMessage(event);
    };
    this.sendMessage = (message, callback) => {
      var _this$iFrame$ref;
      if (!((_this$iFrame$ref = this.iFrame.ref) !== null && _this$iFrame$ref !== void 0 && (_this$iFrame$ref = _this$iFrame$ref.current) !== null && _this$iFrame$ref !== void 0 && _this$iFrame$ref.contentWindow)) {
        return;
      }
      if (callback && this.validCallback(callback)) {
        this.callbacks.set(message.id, callback);
      }
      this.iFrame.ref.current.contentWindow.postMessage(message, this.iFrame.origin);
      logHostMessageSent(this.props.name, message);
    };
    this.handleEmbeddedPassthruPropsChange = embeddedProps => {
      const embeddedPropsChangeMessage = createEmbeddedPropsChangeMessage(this.iFrame.uuid, embeddedProps);
      this.sendMessage(embeddedPropsChangeMessage);
    };
    this.handleReceiveMessage = event => {
      if (!validMessage(event, this.iFrame.origin)) {
        return;
      }
      const {
        data: message
      } = event;
      logHostMessageReceived(this.props.name, message);
      if (this.hasCallback(message)) {
        this.dispatchCallback(message);
      } else if (this.isFromEmbed(message)) {
        if (isReadyMessage(message)) {
          this.handleIFrameReady(message);
        } else if (isToIFrameHost(message)) {
          this.props.onMessage(message);
        }
      } else if (this.isToEmbed(message)) {
        this.sendMessage(message);
      }
    };
    this.handleIFrameReady = message => {
      this.iFrame.ready = true;
      this.props.onReady(createHostContext(this.iFrame.uuid, this.sendMessage), message.payload);
    };
    this.handleInitTimeout = () => {
      const {
        name,
        onInitError
      } = this.props;
      logHostError(name, ERROR_UNABLE_TO_SYNC);
      onInitError(ERROR_UNABLE_TO_SYNC);
    };
    this.handleReadyTimeout = () => {
      var _this$iFrame$ref2;
      this.handleReadyTimer = null;
      if (this.iFrame.ready || !((_this$iFrame$ref2 = this.iFrame.ref) !== null && _this$iFrame$ref2 !== void 0 && _this$iFrame$ref2.current)) {
        return;
      }
      const {
        name,
        onReadyError
      } = this.props;
      logHostError(name, ERROR_EMBED_NOT_READY);
      onReadyError(ERROR_EMBED_NOT_READY);
    };
    this.handleSyncAckMessage = () => {
      this.iFrame.synced = true;
      this.handleReadyTimer = setTimeout(this.handleReadyTimeout, this.props.readyTimeout);
    };
    this.retrySendSync = () => {
      var _this$iFrame$ref3;
      this.retrySyncTimer = null;
      if (this.numSyncRetries === this.maxNumSyncRetries) {
        this.handleInitTimeout();
        return;
      }
      if (!this.iFrame.synced && (_this$iFrame$ref3 = this.iFrame.ref) !== null && _this$iFrame$ref3 !== void 0 && _this$iFrame$ref3.current) {
        this.sendSync();
      }
    };
    this.sendSync = () => {
      const {
        appInfo,
        appName,
        embeddedPassthruProps,
        group,
        name
      } = this.props;
      const appData = createAppData(appInfo, appName);
      const syncMessage = createSyncMessage({
        embedId: this.iFrame.uuid,
        group,
        name,
        appData,
        embeddedProps: embeddedPassthruProps
      });
      this.sendMessage(syncMessage, this.handleSyncAckMessage);
      this.numSyncRetries++;
      this.retrySyncTimer = setTimeout(this.retrySendSync, this.syncRetryInterval);
    };
    this.clearPendingTimers = () => {
      const timers = [this.handleReadyTimer, this.retrySyncTimer];
      timers.filter(isNotNull).forEach(clearTimeout);
    };
    this.maxNumSyncRetries = props.initTimeout / this.syncRetryInterval;
    const iFrameSrc = getIFrameSrc(props.name, props.src);
    this.iFrame = {
      origin: getOrigin(iFrameSrc),
      src: iFrameSrc,
      ref: props.innerRef || /*#__PURE__*/createRef(),
      synced: false,
      ready: false,
      uuid: UUID()
    };
  }
  componentDidMount() {
    this.props._onTestContextReady({
      iFrame: this.iFrame,
      simulatePostMessage: this._simulatePostMessage
    });
    window.addEventListener('message', this.handleReceiveMessage, false);
    this.sendSync();
  }
  shouldComponentUpdate(nextProps) {
    if (this.props.embeddedPassthruProps !== nextProps.embeddedPassthruProps) {
      this.handleEmbeddedPassthruPropsChange(nextProps.embeddedPassthruProps);
    }
    return this.props.allow !== nextProps.allow || this.props.height !== nextProps.height || this.props.iframePassthruProps !== nextProps.iframePassthruProps || this.props.scrolling !== nextProps.scrolling || this.props.src !== nextProps.src || this.props.width !== nextProps.width;
  }
  componentWillUnmount() {
    window.removeEventListener('message', this.handleReceiveMessage);
    this.clearPendingTimers();
  }

  // used for testing only

  render() {
    const {
      'data-test-id': dataTestId,
      height,
      iframePassthruProps,
      scrolling,
      width,
      allow
    } = this.props;
    return /*#__PURE__*/_jsx(StyledIFrame, Object.assign({
      "data-test-id": dataTestId,
      frameBorder: 0
    }, iframePassthruProps, {
      allow: allow,
      height: String(height),
      ref: this.iFrame.ref,
      scrolling: scrolling ? 'yes' : 'no',
      src: this.iFrame.src,
      width: String(width)
    }));
  }
}
IFrame.defaultProps = {
  allow: '',
  appInfo: {},
  appName: '',
  embeddedPassthruProps: {},
  group: 'iframeable',
  height: 200,
  iframePassthruProps: {},
  initTimeout: 8000,
  name: 'embed',
  onInitError: emptyFunction,
  onMessage: emptyFunction,
  onReadyError: emptyFunction,
  readyTimeout: 3000,
  scrolling: true,
  width: 200,
  _onTestContextReady: emptyFunction
};
export const IFrameWithRef = /*#__PURE__*/forwardRef((props, ref) => {
  if (typeof ref === 'function') {
    throw new Error('Callback refs are not supported yet.');
  }
  return /*#__PURE__*/_jsx(IFrame, Object.assign({}, props, {
    innerRef: ref
  }));
});
export default IFrame;